'use client';

import { useEffect, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Menu, Package, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { cn } from '@kit/ui/utils';

import { BallersjoHeaderAccountSection } from './header-account-section';

const navLinks = [
  { labelKey: 'landing:nav.aboutUs', fallback: 'About Us', href: '/#about' },
  { labelKey: 'landing:nav.padel', fallback: 'Padel', href: '/#padel' },
  { labelKey: 'landing:nav.academy', fallback: 'Academy', href: '/#academy' },
  { labelKey: 'landing:nav.programs', fallback: 'Programs', href: '/#programs' },
];

const authNavLinks = [
  {
    labelKey: 'landing:nav.myBookings',
    fallback: 'My Bookings',
    href: '/bookings',
    icon: Calendar,
  },
  {
    labelKey: 'landing:nav.myPackages',
    fallback: 'My Packages',
    href: '/packages',
    icon: Package,
  },
];

const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/faq',
  '/event',
  '/coaches',
  '/membership',
  '/privacy-policy',
  '/terms-of-service',
];

export function BallersjoHeader() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useSession();
  const isAuthenticated = !!session.data;

  const isLandingPage = pathname === '/';
  const shouldBeSolid = solidNavbarRoutes.some((route) =>
    pathname?.startsWith(route),
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const isOpaque = mobileMenuOpen || shouldBeSolid || scrolled;

  return (
    <>
      <header
        className={cn(
          'fixed top-0 right-0 left-0 z-50 transition-all duration-300',
          'bg-white text-black',
          isOpaque
            ? 'border-b border-black/10 py-3 shadow-sm'
            : 'py-4',
        )}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-12">
          {/* Figma node 703:1216 — header logo wordmark */}
          <Link href="/" className="relative z-10 flex-shrink-0" data-node-id="703:1216">
            <Image
              src="/landing-pages/ballersjo/images/header-logo.png"
              alt="Ballers Jordan"
              width={3278}
              height={376}
              className="h-7 w-auto lg:h-8"
              priority
            />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex xl:gap-9">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-[14px] font-medium text-black/85 transition-colors duration-200 hover:text-black"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated &&
              authNavLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-center gap-2 text-[14px] font-medium text-black/85 transition-colors duration-200 hover:text-black"
                >
                  <link.icon className="h-4 w-4" />
                  {t(link.labelKey, { defaultValue: link.fallback })}
                </Link>
              ))}
            <Link
              href="/contact"
              className="ballers-btn-dark inline-flex items-center justify-center bg-black px-5 py-2.5 text-[12px] font-semibold tracking-[0.14em] text-white uppercase"
            >
              {t('landing:nav.contactUs', { defaultValue: 'Contact Us' })}
            </Link>
          </nav>

          <div className="hidden items-center lg:flex">
            <BallersjoHeaderAccountSection variant="light" />
          </div>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="-mr-2 p-2 text-black lg:hidden"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <div
            className="fixed inset-0 top-[64px] z-40 bg-black/30 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        <div
          className={cn(
            'absolute top-full right-0 left-0 z-50 overflow-hidden bg-white transition-all duration-300 lg:hidden',
            mobileMenuOpen
              ? 'max-h-[600px] border-b border-black/10'
              : 'max-h-0',
          )}
        >
          <nav className="flex flex-col px-6 pb-5">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="border-b border-black/10 py-3 text-[14px] font-medium text-black/85 transition-colors hover:text-black"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated && (
              <>
                {authNavLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 border-b border-black/10 py-3 text-[14px] font-medium text-black/85 transition-colors hover:text-black"
                    >
                      <Icon className="h-4 w-4" />
                      {t(link.labelKey, { defaultValue: link.fallback })}
                    </Link>
                  );
                })}
              </>
            )}
            <Link
              href="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-4 inline-flex items-center justify-center bg-black px-5 py-3 text-[12px] font-semibold tracking-[0.14em] text-white uppercase"
            >
              {t('landing:nav.contactUs', { defaultValue: 'Contact Us' })}
            </Link>
            <div className="pt-4">
              <BallersjoHeaderAccountSection
                variant="light"
                onNavigate={() => setMobileMenuOpen(false)}
              />
            </div>
          </nav>
        </div>
      </header>
      {!isLandingPage && <div className="h-[64px]" />}
    </>
  );
}

function useSession() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: listener } = client.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
    });

    return () => listener.subscription.unsubscribe();
  }, [client.auth, queryClient]);

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}
